import { useEffect, useRef, useState } from 'react';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { stripCloneSuffix } from '@/features/datasets/utils/datasetCloneNames';
import { fetchLibraryDatasetSample, LIBRARY_PREVIEW_SAMPLE_LIMIT } from '@/features/datasets/utils/libraryPickDefaults';
import {
    applyAiGeneratedColumnDefaults,
    dataCreationForUi,
    isAiGeneratedDataset,
    knownLabelsForAiGeneration,
    LLM_CREATED_DATA_CREATION,
} from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import { sourceFieldsFromDatasetInfoDoc } from '@/features/datasets/utils/datasetSourceConfig';
import {
    NO_LABELS_MODE_DEFAULT,
    NO_LABELS_MODE_UNLABELED,
} from '@/features/datasets/utils/hasLabelsHandling';
import { emptyFileEntry } from './useWizardDataUpload';
import { emptyDetails } from './DataCollectionPanel';
import { fieldsInTemplate } from './promptMapper/promptMappingUtils';
import { consumeDataCollectionStash } from './settingsReturnStash';
import { trajectoryDetailsFromColumnMappings } from './trajectory/trajectoryMapping';

/** Full dataset_info docs (column_mappings, data_creation_type, …) linked to the usecase. */
export function fetchUsecaseDatasetInfos(usecase) {
    return new Promise((resolve) => {
        data_from_server_post(
            'datasets-and-benchmarks-meta-data',
            { client_id: usecase.client_id, llm_usecase_id: usecase._id },
            (result) => resolve(result?.datasets_info || []),
            () => resolve([])
        );
    });
}

/**
 * Dropdown columns for a persisted dataset_info doc. Unlabeled uploads used
 * to store only mapped columns (`example`) in `file_column_names`; the
 * original labels column still lives in `extra_params_keys`. Union those
 * with mapped example/label/relations names so resume-after-refresh can
 * re-select the labels column.
 */
export function columnsFromDatasetInfoDoc(doc) {
    const names = new Set();
    const add = (value) => {
        if (value == null) return;
        const text = String(value).trim();
        if (text) names.add(text);
    };
    (doc?.file_column_names || []).forEach(add);
    (doc?.extra_params_keys || []).forEach(add);
    const mappings = doc?.column_mappings || {};
    add(mappings.example_column_name);
    add(mappings.label_column_name);
    add(mappings.relations_column_name);
    return [...names];
}

function applyDocColumnDefaults(doc, { taskType, usecaseName } = {}) {
    const unlabeled = Boolean(doc?.column_mappings?.import_without_labels);
    if (unlabeled) {
        return {
            columns: columnsFromDatasetInfoDoc(doc),
            mappings: { ...(doc?.column_mappings || {}) },
        };
    }
    return applyAiGeneratedColumnDefaults({
        columns: columnsFromDatasetInfoDoc(doc),
        mappings: doc?.column_mappings || {},
        taskType,
        dataCreationType: doc?.data_creation_type,
        datasetName: doc?.dataset,
        usecaseName,
    });
}

/**
 * File-less upload-slot entry for an attached dataset — the same shape
 * `upload.submit` leaves behind after an in-session Next (uploadName without
 * the clone suffix, `file: null`, columns for the dropdowns).
 */
export function entryFromDoc(doc, { taskType, usecaseName } = {}) {
    const { columns } = applyDocColumnDefaults(doc, { taskType, usecaseName });
    return {
        ...emptyFileEntry,
        id: `hydrated-${doc.dataset}`,
        uploadName: stripCloneSuffix(doc.dataset) || doc.dataset,
        datasetName: doc.dataset,
        columns,
        dataCreationType: doc.data_creation_type,
        ...sourceFieldsFromDatasetInfoDoc(doc),
    };
}

/** Rebuild one target's details form from its persisted dataset_info doc. */
export function detailsFromDoc(
    doc,
    distinctLabels,
    { taskType, usecaseName, isMultilabel, usecase, formLabels } = {}
) {
    const { mappings } = applyDocColumnDefaults(doc, { taskType, usecaseName });
    const labelColumn = mappings.label_column_name || '';
    const hasLabels = Boolean(labelColumn);
    const isAi = isAiGeneratedDataset({
        dataCreationType: doc?.data_creation_type,
        datasetName: doc?.dataset,
        usecaseName,
    });
    const storedMappings =
        mappings.label_mappings && Object.keys(mappings.label_mappings).length
            ? mappings.label_mappings
            : null;
    const extractedLabels = storedMappings
        ? Object.keys(storedMappings)
        : distinctLabels?.length
          ? distinctLabels
          : isAi
            ? knownLabelsForAiGeneration({
                  formLabels,
                  usecase,
                  taskType,
              })
            : [];
    const hasTemplate = Boolean(mappings.examples_are_with_prompt && mappings.prompt_template);
    // Restore the Map-prompt-template state: the saved primary (older docs
    // without example_dynamic_field fall back to the first template field)
    // and template fields that are not file columns as custom fields (their
    // original texts are not persisted, so they restore as empty).
    const templateFields = hasTemplate ? fieldsInTemplate(mappings.prompt_template) : [];
    const primaryModelInput = hasTemplate
        ? (templateFields.includes(mappings.example_dynamic_field) &&
              mappings.example_dynamic_field) ||
          (templateFields.includes('example') ? 'example' : templateFields[0] || null)
        : null;
    const docColumns = new Set(columnsFromDatasetInfoDoc(doc));
    const promptCustomFields = Object.fromEntries(
        templateFields.filter((field) => !docColumns.has(field)).map((field) => [field, ''])
    );
    return {
        ...emptyDetails(),
        dataCreation:
            dataCreationForUi(doc?.data_creation_type) || (isAi ? LLM_CREATED_DATA_CREATION : null),
        exampleColumn: mappings.example_column_name || null,
        labelColumn,
        hasLabels,
        noLabelsMode:
            !hasLabels && mappings.import_without_labels
                ? NO_LABELS_MODE_UNLABELED
                : NO_LABELS_MODE_DEFAULT,
        ...(isMultilabel && hasLabels
            ? {
                  labelsDelimiter:
                      mappings.classification_labels_delimiter || (isAi ? 'list' : null),
              }
            : {}),
        extractedLabels: hasLabels ? extractedLabels : [],
        labelMappings: hasLabels
            ? storedMappings || Object.fromEntries(extractedLabels.map((label) => [label, label]))
            : {},
        customInstructions: hasTemplate,
        promptTemplate: hasTemplate ? mappings.prompt_template : '',
        primaryModelInput,
        promptCustomFields,
        emptyMultilabelHandling: mappings.empty_multilabel_handling || null,
        // Agentic uploads: trajectory column / message keys / role mapping.
        ...trajectoryDetailsFromColumnMappings(mappings),
    };
}

/** Fields that decide whether dataset and benchmark "share the same details". */
const comparableDetails = (details) =>
    JSON.stringify({
        dataCreation: details.dataCreation,
        exampleColumn: details.exampleColumn,
        labelColumn: details.labelColumn,
        hasLabels: details.hasLabels,
        noLabelsMode: details.noLabelsMode,
        labelMappings: details.labelMappings,
        customInstructions: details.customInstructions,
        promptTemplate: details.promptTemplate,
        primaryModelInput: details.primaryModelInput,
        emptyMultilabelHandling: details.emptyMultilabelHandling,
    });

/**
 * Build a `stateCacheRef`-shaped snapshot of the Data Collection step from
 * the server-persisted dataset_info docs, so resuming a draft restores the
 * step exactly like in-session Back/Next navigation does.
 */
async function buildHydrationSnapshot({ usecase, taskType }) {
    const docs = (await fetchUsecaseDatasetInfos(usecase)).filter(
        (doc) => doc && !doc.is_manual_model_predictions
    );
    const datasetDocs = docs.filter((doc) => !doc.is_benchmark);
    const benchmarkDocs = docs.filter((doc) => doc.is_benchmark);
    if (!datasetDocs.length && !benchmarkDocs.length) return null;

    const distinctTarget =
        taskType === TaskType.classification
            ? 'classification'
            : taskType === TaskType.NER
              ? 'ner_entities'
              : null;

    const hydrateOptions = {
        taskType,
        usecaseName: usecase.usecase_name,
        isMultilabel: Boolean(usecase.is_multilabel),
        usecase,
    };
    const hydrateTarget = async (targetDocs, isBenchmark) => {
        if (!targetDocs.length) return { entries: [], details: null };
        const entries = targetDocs.map((doc) => entryFromDoc(doc, hydrateOptions));
        const primary = targetDocs[0];
        const mappings = applyDocColumnDefaults(primary, hydrateOptions).mappings;
        let distinctLabels = [];
        try {
            const sample = await fetchLibraryDatasetSample({
                clientId: usecase.client_id,
                datasetName: primary.dataset,
                isBenchmark,
                labelColumn: mappings.label_column_name || null,
                distinctTarget: mappings.label_column_name ? distinctTarget : null,
                limit: LIBRARY_PREVIEW_SAMPLE_LIMIT,
            });
            if (sample.sample && Object.keys(sample.sample).length) {
                entries[0] = {
                    ...entries[0],
                    sampleRecord: sample.sample,
                    sampleRecords: sample.samples || [sample.sample],
                    previewSampleLimit: LIBRARY_PREVIEW_SAMPLE_LIMIT,
                };
            }
            distinctLabels = sample.distinct_labels || [];
        } catch (error) {
            console.error('Failed to load sample record during hydration:', error);
        }
        return { entries, details: detailsFromDoc(primary, distinctLabels, hydrateOptions) };
    };

    const [dataset, benchmark] = await Promise.all([
        hydrateTarget(datasetDocs, false),
        hydrateTarget(benchmarkDocs, true),
    ]);

    const datasetDetails = dataset.details || emptyDetails();
    const benchmarkDetails = benchmark.details || emptyDetails();
    const shareDetails =
        !benchmark.details ||
        !dataset.details ||
        comparableDetails(datasetDetails) === comparableDetails(benchmarkDetails);

    // Prefer dataset when present (matches primaryDetailsTarget for shared
    // mode). Stamp languagesDetectedFor so the panel does not overwrite
    // persisted task_languages from a single hydrated sampleRecord.
    const primaryEntries = dataset.entries.length ? dataset.entries : benchmark.entries;
    const primaryDetails = dataset.entries.length ? datasetDetails : benchmarkDetails;
    const sampleable = primaryEntries.filter((entry) => entry.content || entry.sampleRecord);
    const exampleColumn = primaryDetails.exampleColumn;
    const languagesDetectedFor =
        exampleColumn && sampleable.length
            ? `${exampleColumn}::${sampleable.map((entry) => entry.id).join('|')}`
            : null;

    return {
        slots: {
            dataset: { files: dataset.entries },
            benchmark: { files: benchmark.entries },
        },
        languages: usecase.task_languages?.length ? [...usecase.task_languages] : ['en'],
        shareDetails,
        detailsByTarget: { dataset: datasetDetails, benchmark: benchmarkDetails },
        defaultedFor: {},
        extractedFor: {},
        languagesDetectedFor,
        // Leave unset so the panel re-seeds the fallback label from the
        // usecase-hydrated Business Goals value.
        seededFallback: undefined,
    };
}

/**
 * Rehydrate the wizard Data Collection step from the server when a draft is
 * resumed: with no in-memory `stateCacheRef` snapshot (the ref dies when the
 * wizard unmounts) but datasets/benchmarks attached to the usecase, fetch
 * their dataset_info docs and rebuild the cache snapshot before the panel
 * mounts. Returns `true` once the panel can render.
 */
export default function useDataCollectionHydration({ usecase, taskType, stateCacheRef }) {
    // "Add credentials" round-trip (no-provider warning → Settings → back):
    // restore the snapshot stashed before the navigation instead of hydrating
    // from the server — it holds state the server does not (locally attached
    // files not yet uploaded, details-form edits). Consume-once and keyed by
    // usecase id, so a later fresh open still hydrates from the server.
    if (stateCacheRef && !stateCacheRef.current && usecase?._id) {
        const stashed = consumeDataCollectionStash(usecase._id);
        if (stashed) stateCacheRef.current = stashed;
    }
    const hasAttachedData = Boolean(
        (usecase?.datasets || []).length || (usecase?.benchmarks || []).length
    );
    const [ready, setReady] = useState(
        () => Boolean(stateCacheRef?.current) || (Boolean(usecase) && !hasAttachedData)
    );
    const startedRef = useRef(false);

    useEffect(() => {
        if (ready || startedRef.current) return;
        if (stateCacheRef?.current || (usecase && !hasAttachedData)) {
            setReady(true);
            return;
        }
        if (!usecase?._id) return;
        startedRef.current = true;
        // One-shot per mount (startedRef): readiness must not be tied to this
        // effect run's lifetime. The `usecase` dep is an object whose identity
        // churns on every websocket refresh (live generation progress, upload
        // status), so a cancelled-on-cleanup flag would discard the finished
        // fetch and leave the step on its spinner forever — later runs bail
        // on startedRef and nothing ever sets ready again.
        (async () => {
            try {
                const snapshot = await buildHydrationSnapshot({ usecase, taskType });
                if (snapshot && stateCacheRef && !stateCacheRef.current) {
                    stateCacheRef.current = snapshot;
                }
            } catch (error) {
                console.error('Data Collection hydration failed:', error);
            }
            setReady(true);
        })();
    }, [ready, usecase, taskType, stateCacheRef, hasAttachedData]);

    return ready;
}
